import linkifyHtml from "linkify-html"
import * as linkify from "linkifyjs"
import "linkify-plugin-hashtag"

import DOMPurify from "dompurify"
import escape from "lodash/escape"
import upperFirst from "lodash/upperFirst"
import md5 from "crypto-js/md5"
import i18n from "@/plugins/i18n"

import { isIos, isMobileAndroid, isFirefox } from "@/services/environment.js"

import { groupTypes } from "@/constants/groups.js"

export const sanitizeHTML = function (html, options = {}) {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true }, ...options })
}

// Returns vue template string with linkified content which can be used with VRuntimeTemplate
export const linkifyContent = function ({ content, mentions = [], hashtags = [] }) {
  if (!content) {
    return ""
  }

  const templateFunction = (item, substringBefore, substringMid, substringAfter) => {
    const beforeEscaped = ignoreMustacheTags(substringBefore)
    const afterEscaped = ignoreMustacheTags(substringAfter)
    const dynamicLink = `<dynamic-link link="${escape(substringMid)}" type="${item.type}" data-id="${item.user_id}"></dynamic-link>`
    return `${beforeEscaped}${dynamicLink}${afterEscaped}`
  }

  let processed = ""
  if (!hashtags?.length && !mentions?.length) {
    processed = ignoreMustacheTags(content)
  } else {
    processed = formatMentionsAndHashtagsWithTemplate({ content, mentions, hashtags }, templateFunction)
  }

  const sanitized = DOMPurify.sanitize(processed, {
    ALLOWED_TAGS: ["dynamic-link", "span"],
    ALLOWED_ATTR: ["link", "type", "data-id", "v-pre"]
  })

  const linkified = linkifyHtml(sanitized, {
    nl2br: true,
    attributes: {
      target: "_blank",
      rel: "noindex"
    },
    className: "linkified"
  })

  return `<pre>${linkified}</pre>`
}

export const getContentWithFormattedMentionsAndHashtags = ({ content, mentions = [], hashtags = [] }) => {
  if (!content) {
    return ""
  }
  const templateFunction = (item, substringBefore, substringMid, substringAfter) => {
    return item.type === "mention"
      ? `${substringBefore}${getMentionItem(substringMid.replace("@", ""))}${substringAfter}`
      : `${substringBefore}<b contenteditable="false">${substringMid}</b>${substringAfter}`
  }
  return formatMentionsAndHashtagsWithTemplate({ content, mentions, hashtags }, templateFunction)
}

export const getMentionItem = (content) => {
  const escaped = escapeText(content)
  const mentionAttrs = isIos() || isMobileAndroid() ? `class="mention-item" data-initial="${escaped}"` : 'contenteditable="false"'

  return `<b ${mentionAttrs}>@${escaped}</b>`
}

let splitter

//https://cestoliv.com/blog/how-to-count-emojis-with-javascript/
//Remove this when Firefox supports Intl.Segmenter (that's the only browser not supported),
//Graphemer is still not perfect but better than any other solutions (Array.form, [...str], lodash/split etc.)
export const setupSplitter = async () => {
  if (!Intl.Segmenter) {
    const { default: Graphemer } = await import("graphemer")
    splitter = new Graphemer()
  }
}

const splitContent = (str) => {
  if (splitter) {
    return splitter.splitGraphemes(str)
  }
  return splitGraphemesNative(str)
}

const splitGraphemesNative = (str) => {
  //Break into chunks to avoid memory overload errors
  const chunkSize = 5000
  let characters = []
  const segmenter = new Intl.Segmenter()

  for (let i = 0; i < str.length; i += chunkSize) {
    const chunk = str.substr(i, chunkSize)
    const segments = [...segmenter.segment(chunk)].map((s) => s.segment)

    characters = characters.concat(segments)
  }

  return characters
}

const formatMentionsAndHashtagsWithTemplate = ({ content, mentions = [], hashtags = [] }, template) => {
  const contentArray = splitContent(content)

  let formatted = ""

  hashtags.forEach((h) => (h.type = "hashtag"))
  mentions.forEach((m) => (m.type = "mention"))

  const links = hashtags.concat(mentions)
  links.sort((a, b) => a.indices[0] - b.indices[0])

  for (let i = 0; i < links.length; i++) {
    const item = links[i]

    const substringBefore = contentArray.slice(links[i - 1] ? links[i - 1].indices[1] + 1 : 0, item.indices[0]).join("")
    const substringMid = contentArray.slice(item.indices[0], item.indices[1] + 1).join("")
    const substringAfter = links[i + 1] ? "" : contentArray.slice(item.indices[1] + 1).join("")

    formatted += template(item, substringBefore, substringMid, substringAfter)

    if (isFirefox() && i === links.length - 1 && substringAfter === "") {
      //Add space at the end if the last element is mention
      //Otherwise firefox doesn't put caret at the end when we click at the end of input
      formatted += " "
    }
  }

  return formatted
}

const ignoreMustacheTags = (content) => {
  return `<span v-pre>${content}</span>`
}

export const findUrls = function (content, ignoredUrls = [], wholeText = false) {
  if (!wholeText && !/\s+$/.test(content)) {
    return null
  }
  const urls = linkify.find(content, "url")
  if (urls.length) {
    const firstNotIgnoredUrl = urls.find((u) => !ignoredUrls.includes(u.href))

    if (!firstNotIgnoredUrl) {
      return null
    }

    const isUrlAtTheEnd = content.trim().endsWith(firstNotIgnoredUrl.value)
    if (wholeText || isUrlAtTheEnd) {
      return firstNotIgnoredUrl.href
    }
  }

  return null
}

// Returns vue template string with linkified content which can be used with VRuntimeTemplate
export const linkifyRawText = function (content) {
  if (!content) {
    return ""
  }

  const hashtagsDistinct = linkify
    .find(content, "hashtag")
    .map((h) => h.value)
    .filter((v, i, a) => a.indexOf(v) === i)

  const occurences = []

  hashtagsDistinct.forEach((hashtag) => {
    const indices = getIndicesOfStringInContent(content, hashtag)

    indices.forEach((i) =>
      occurences.push({
        value: hashtag,
        indices: [i, i + (hashtag.length - 1)]
      })
    )
  })

  return linkifyContent({ content, hashtags: occurences, mentions: [] })
}

const getIndicesOfStringInContent = function (content, search) {
  //Need to use grapheme splitter because of emojis
  //It's suboptimal but don't see better way to handle those
  const segments = splitContent(content)

  const indices = []

  for (let i = 0; i < segments.length; i++) {
    if (segments[i] === search[0] && i + search.length <= segments.length && segments.slice(i, i + search.length).every((s, index) => s === search[index])) {
      indices.push(i)
    }
  }

  return indices
}

export const escapeText = function (text) {
  const el = document.createElement("span")
  el.textContent = text
  return el.innerHTML
}

export const toMd5 = function (text) {
  return md5(text).toString()
}

// Eg 1000 -> 1K, 999100 -> 999.1K
export const formatShortNumber = function (number) {
  if (number == null) {
    return ""
  }

  const k = 1000
  const m = k * k

  if (number < k) {
    return number
  }

  const divider = number < m ? k : m

  const divided = number / divider
  const decPartStartsWith0 = divided % 1 < 0.1
  const formattedOneDecimalWithoutRounding = divided.toString().match(/^-?\d+(?:\.\d{0,1})?/)[0]

  const thoudsandUnit = i18n.global.t("texts.thousand").toUpperCase()
  const milionUnit = i18n.global.t("texts.milion").toUpperCase()

  return `${decPartStartsWith0 ? Math.floor(divided) : formattedOneDecimalWithoutRounding}${divider === k ? thoudsandUnit : milionUnit}`
}

export const formatPeriod = function (periodType) {
  switch (periodType) {
    case "day":
      return i18n.global.t("texts.daily")
    case "month":
      return i18n.global.t("common.monthly")
    case "year":
      return i18n.global.t("texts.annually")
    default:
      return ""
  }
}

export const formatCurrency = function ({ priceInCents, currencyType, subunitToUnit, trimCentsIfZero }) {
  const locale = navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language || "en-US"
  const price = (priceInCents / subunitToUnit).toFixed(2)
  let formattedString = new Intl.NumberFormat(locale, { style: "currency", currency: currencyType }).format(price)

  if (trimCentsIfZero) {
    formattedString = formattedString.replace(".00", "")
  }

  return formattedString
}

export const formatPricePerPeriod = function ({ priceInCents, currencyType, subunitToUnit, periodType }) {
  const formattedCurrency = formatCurrency({ priceInCents, subunitToUnit, currencyType })
  switch (periodType) {
    case "day":
      return i18n.global.t("texts.perDay", { count: formattedCurrency })
    case "month":
      return i18n.global.t("texts.perMonth", { count: formattedCurrency })
    case "year":
      return i18n.global.t("texts.perYear", { count: formattedCurrency })
    default:
      return ""
  }
}

export const generateUUID = function () {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const groupTypeText = function (group) {
  if (!group.membership_type || group.membership_type === groupTypes.MANDATORY) {
    return null
  }
  return upperFirst(i18n.global.t("texts.group", { groupType: group.membership_type }))
}

export const membersCountText = function (group) {
  if (!group.members_count || !group.members_viewable) {
    return null
  }

  return i18n.global.t(
    "common.members",
    {
      count: formatShortNumber(group.members_count)
    },
    group.members_count
  )
}

export const mimeTypeToDescription = function (mimeType) {
  switch (mimeType) {
    case "text/csv":
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return i18n.global.t("texts.spreasheet")
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return i18n.global.t("texts.slideDeck")
    default:
      return i18n.global.t("texts.document")
  }
}
